import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { getInlineCitationLinkClassName, linkCitationChunk } from '../../../utils/citationDisplay';
import citationStyles from '../../Dataroom2.0/css/index.module.css';
import CodeBlock from './CodeBlock';
import MermaidBlock from './MermaidBlock';
import MarkdownImage from './MarkdownImage';
import {
    buildMarkdownRenderCacheKey,
    findCitationByRef,
    isClosedFenceBlock,
    isExternalHref,
    isSafeUrl,
    linkifyCitationRefs,
    parseCalloutFromBlockquote,
} from './markdownUtils';
import { stripIncompleteMathForStreaming } from '../streaming/streamCompletionDetector';
import styles from './richMarkdown.module.css';

function createMarkdownComponents({
    content,
    citations,
    chunks,
    citationScope,
    onCitationClick,
    streaming = false,
    streamingComplete = true,
}) {
    return {
        a: ({ href, children, ...props }) => {
            const anchorProps = { ...props };
            delete anchorProps.node;
            const match =
                typeof href === 'string' ? href.match(/^#ai-warden-[a-z0-9-]+-citation-(\d+)$/i) : null;
            if (match) {
                const cite = findCitationByRef(citations, match[1]);
                const linkedChunk = cite ? linkCitationChunk(cite, chunks) : null;
                const linkClass =
                    getInlineCitationLinkClassName(citationStyles, cite, linkedChunk) ||
                    citationStyles.aiWardenInlineCitationLink;
                return (
                    <a
                        {...anchorProps}
                        href={href}
                        className={linkClass}
                        onClick={event => {
                            if (!onCitationClick) return;
                            event.preventDefault();
                            onCitationClick?.(match[1], cite, linkedChunk);
                        }}>
                        {children}
                    </a>
                );
            }

            if (!href || !isSafeUrl(href)) {
                return <span>{children}</span>;
            }

            const external = isExternalHref(href);
            return (
                <a
                    {...anchorProps}
                    href={href}
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                    {children}
                </a>
            );
        },
        blockquote: ({ children }) => {
            const callout = parseCalloutFromBlockquote(children);
            if (callout) {
                const calloutClass =
                    callout.type === 'warning'
                        ? styles.calloutWarning
                        : callout.type === 'important'
                          ? styles.calloutImportant
                          : styles.calloutNote;
                return (
                    <aside className={`${styles.callout} ${calloutClass}`} role="note">
                        <div className={styles.calloutLabel}>{callout.type}</div>
                        <div className={styles.calloutBody}>{callout.body}</div>
                    </aside>
                );
            }
            return <blockquote className={styles.blockquote}>{children}</blockquote>;
        },
        code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1].toLowerCase() : '';
            const codeText = String(children).replace(/\n$/, '');

            if (match && language === 'mermaid') {
                const fenceComplete = isClosedFenceBlock(content, 'mermaid');
                return (
                    <MermaidBlock
                        code={codeText}
                        complete={!streaming || fenceComplete}
                    />
                );
            }

            if (match) {
                const fenceComplete = isClosedFenceBlock(content, language);
                return (
                    <CodeBlock
                        language={language}
                        code={codeText}
                        streaming={streaming}
                        fenceComplete={fenceComplete}
                        streamingComplete={streamingComplete}
                    />
                );
            }

            return (
                <code className={styles.inlineCode} {...props}>
                    {children}
                </code>
            );
        },
        pre: ({ children }) => <>{children}</>,
        table: ({ children, ...props }) => (
            <div className={styles.tableScroll}>
                <table className={styles.table} {...props}>
                    {children}
                </table>
            </div>
        ),
        img: ({ src, alt, title }) => <MarkdownImage src={src} alt={alt} title={title} />,
        hr: () => <hr className={styles.hr} />,
    };
}

function RichMarkdown({
    content,
    citations = [],
    chunks = [],
    citationScope = '',
    onCitationClick,
    className = '',
    streaming = false,
    streamingComplete = true,
}) {
    const text = typeof content === 'string' ? content : '';
    const isStreamingActive = streaming && !streamingComplete;
    const cacheKey = useMemo(
        () => buildMarkdownRenderCacheKey(text, citations, citationScope, isStreamingActive),
        [text, citations, citationScope, isStreamingActive],
    );
    const renderedText = useMemo(() => {
        let value = linkifyCitationRefs(text, citations, citationScope);
        if (isStreamingActive) {
            value = stripIncompleteMathForStreaming(value);
        }
        return value;
    }, [cacheKey, text, citations, citationScope, isStreamingActive]);
    const remarkPlugins = useMemo(() => [remarkGfm, remarkMath], []);
    const rehypePlugins = useMemo(
        () => (isStreamingActive ? [] : [rehypeKatex]),
        [isStreamingActive],
    );
    const components = useMemo(
        () =>
            createMarkdownComponents({
                content: text,
                citations,
                chunks,
                citationScope,
                onCitationClick,
                streaming: isStreamingActive,
                streamingComplete,
            }),
        [text, citations, chunks, citationScope, onCitationClick, isStreamingActive, streamingComplete],
    );

    if (!text.trim() && !isStreamingActive) return null;

    return (
        <div className={`${styles.richMarkdown} ${className || ''}`.trim()}>
            {text.trim() ? (
                <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
                    {renderedText}
                </ReactMarkdown>
            ) : null}
            {isStreamingActive ? <span className={styles.streamingCursor} aria-hidden /> : null}
        </div>
    );
}

export default memo(RichMarkdown);
